import React, { useCallback, useEffect, useState } from 'react';
import { CONTENT_TOP } from 'dashboard/layouts/admin/chromeGeometry';
import {
  Box,
  Button,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useOutletContext } from 'react-router';
import { toast } from 'react-toastify';
import { API } from 'utilities/api';
import {
  Code,
  EmptyState,
  LoadingOverlay,
  PageHeader,
  PagePaper,
  StatusChip,
} from 'dashboard/components/common';
import HookDialog from './HookDialog';
import DeliveriesDialog from './DeliveriesDialog';

const HOOK_STATUS_COLORS = { active: 'success', paused: 'default' };
const LAST_DELIVERY_COLORS = (code) =>
  code >= 200 && code < 300 ? 'success' : 'error';

/**
 * Outbound webhook subscriptions list. Owner CRUD on the brand's
 * webhook endpoints; per-row "Deliveries" opens the recent-attempts
 * log so they can verify what their endpoint is actually receiving.
 */
export default function Webhooks() {
  const [selectedBrand] = useOutletContext();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editing, setEditing] = useState(null);
  const [delHook, setDelHook] = useState(null);
  const [editOpen, setEditOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = useCallback(() => {
    if (!selectedBrand) return Promise.resolve();
    setLoading(true);
    return API.listBrandWebhooks({ brand_id: selectedBrand })
      .then((res) => setItems(Array.isArray(res) ? res : []))
      .catch((err) => toast.error(err?.message || 'Failed to load webhooks', { toastId: 'webhooks-load-error' }))
      .finally(() => setLoading(false));
  }, [selectedBrand]);

  useEffect(() => {
    if (!selectedBrand) return undefined;
    let cancelled = false;
    setLoading(true);
    API.listBrandWebhooks({ brand_id: selectedBrand })
      .then((res) => { if (!cancelled) setItems(Array.isArray(res) ? res : []); })
      .catch((err) => { if (!cancelled) toast.error(err?.message || 'Failed to load webhooks', { toastId: 'webhooks-load-error' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [selectedBrand]);

  const rotate = (hook) => {
    if (!window.confirm('Rotate this webhook secret? The current one will stop working immediately.')) return;
    API.rotateBrandWebhookSecret({ brand_id: selectedBrand, _id: hook._id })
      .then((res) => {
        if (res?.secret) {
          navigator.clipboard?.writeText(res.secret).then(
            () => toast.success('New secret copied to clipboard'),
            () => toast.info('Secret rotated (copy manually from the response)')
          );
        }
        load();
      })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  const remove = (hook) => {
    if (!window.confirm(`Delete the webhook to ${hook.url}?`)) return;
    API.deleteBrandWebhook({ brand_id: selectedBrand, _id: hook._id })
      .then(() => { toast.success('Deleted'); load(); })
      .catch((err) => toast.error(err?.message || 'Failed'));
  };

  return (
    <Box sx={{ pt: CONTENT_TOP }}>
      <PagePaper>
        <PageHeader
          title='Webhooks'
          subtitle={
            <>
              We POST a signed JSON envelope to each URL when the matching event fires.
              Verify each delivery via the <Code>X-TPO-Signature</Code> header (HMAC SHA-256 of the body, using the webhook's secret).
            </>
          }
          actions={
            <Button
              variant='contained'
              startIcon={<AddIcon />}
              onClick={() => { setEditing(null); setEditOpen(true); }}
            >
              New webhook
            </Button>
          }
        />

        {!selectedBrand && <EmptyState>Select a brand.</EmptyState>}

        {selectedBrand && (
          <LoadingOverlay loading={loading && items.length === 0} minHeight={160}>
          <TableContainer
            component={Box}
            sx={{
              border: '1px solid', borderColor: 'divider',
              borderRadius: '12px', overflowX: 'auto',
            }}
          >
            <Table size='medium'>
              <TableHead sx={{ bgcolor: 'action.hover' }}>
                <TableRow>
                  <TableCell>URL</TableCell>
                  <TableCell>Events</TableCell>
                  <TableCell>Last delivery</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell align='right'>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}><EmptyState>Loading…</EmptyState></TableCell>
                  </TableRow>
                )}
                {!loading && items.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={5}><EmptyState>No webhooks yet.</EmptyState></TableCell>
                  </TableRow>
                )}
                {items.map((h) => (
                  <TableRow key={h._id}>
                    <TableCell sx={{ maxWidth: 320 }}>
                      {h.name && <Typography fontWeight='700'>{h.name}</Typography>}
                      <Typography variant='caption' color='text.secondary' noWrap display='block'>
                        {h.url}
                      </Typography>
                      <Typography variant='caption' color='text.secondary'>
                        secret {h.secret_preview}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant='caption'>
                        {h.events && h.events.length > 0 ? h.events.join(', ') : 'all events'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {h.last_delivery_at ? (
                        <>
                          <StatusChip
                            status={h.last_delivery_status}
                            label={h.last_delivery_status || '—'}
                            colors={{
                              [h.last_delivery_status]: LAST_DELIVERY_COLORS(h.last_delivery_status),
                            }}
                          />
                          <Typography variant='caption' color='text.secondary' display='block' mt='2px'>
                            {new Date(h.last_delivery_at).toLocaleString()}
                          </Typography>
                          {h.failure_count > 0 && (
                            <Typography variant='caption' color='error.main' display='block' mt='2px'>
                              {h.failure_count} consecutive failure{h.failure_count === 1 ? '' : 's'}
                            </Typography>
                          )}
                        </>
                      ) : (
                        <Typography variant='caption' color='text.secondary'>—</Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <StatusChip status={h.status} colors={HOOK_STATUS_COLORS} />
                    </TableCell>
                    <TableCell align='right'>
                      <Stack direction='row' spacing='4px' justifyContent='flex-end' flexWrap='wrap'>
                        <Button
                          size='small'
                          variant='outlined'
                          onClick={() => { setDelHook(h); setDelOpen(true); }}
                        >
                          Deliveries
                        </Button>
                        <Tooltip title='Edit'>
                          <IconButton size='small' onClick={() => { setEditing(h); setEditOpen(true); }}>
                            <EditIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Rotate secret'>
                          <IconButton size='small' onClick={() => rotate(h)}>
                            <RefreshIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title='Delete'>
                          <IconButton size='small' color='error' onClick={() => remove(h)}>
                            <DeleteIcon fontSize='small' />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          </LoadingOverlay>
        )}
      </PagePaper>

      <HookDialog
        open={editOpen}
        onClose={() => setEditOpen(false)}
        editing={editing}
        onSaved={load}
        brandId={selectedBrand}
      />
      <DeliveriesDialog
        open={delOpen}
        onClose={() => setDelOpen(false)}
        brandId={selectedBrand}
        hook={delHook}
      />
    </Box>
  );
}
